<template>
  <article class="page page-orders page-order-details">
    <AppSpinner v-if="loading" />

    <template v-else>
      <section class="banner">
        <h1>Photos</h1>

        <AppButton
          class="btn btn-outline btn-secondary rounded-none"
          @click="authStore.isAuthenticated ? redirect() : showAuthenticationModal = true"
        >
          View all orders
        </AppButton>
      </section>

      <section class="order-items">
        <!--photos-->
        <ul class="items">
          <template
            v-for="item in order.items"
            :key="item.id"
          >
            <OrderDetailsItem
              :item="item"
              :order="order"
              :is-photo-pack="item.type === PhotoType.Pack"
            />
          </template>
        </ul>
      </section>

      <!-- Authentication modal -->
      <Transition name="fade">
        <AuthenticationModal
          v-if="showAuthenticationModal"
          :intent="IntentType.Login"
          @submit="redirect"
          @close="showAuthenticationModal = false"
        />
      </Transition>
    </template>
  </article>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { AppButton, AppSpinner } from '@/components/app';
import { OrderDetailsItem } from '@/components/orders';
import { AuthenticationModal } from '@/components/modals/';

import { useHttp } from '@/composables';
import { IntentType, PhotoType } from '@/const';
import { useAuthStore } from '@/store';

const authStore = useAuthStore();

const order = ref({});
const loading = ref(true);
const route = useRoute();
const router = useRouter();

const showAuthenticationModal = ref(false);

const loadOrder = async () => {
  loading.value = true;

  const response = await useHttp().get(`/orders/shared/${route.params.share}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    order.value = response.data;
  }

  loading.value = false;
};

const refreshOrders = async () => {
  let attempts = 0;
  const initialMaxAttempts = 20;
  const initialInterval = 10000;
  let subsequentInterval = initialInterval;
  let missingDownloadableFile = null;

  const attemptLoad = async () => {
    // After the initial load, the API call will be made after 10 seconds at set intervals.
    // Only if the status or the items on the Order have changed will the actual order object be updated with the new data from the response,
    // this way the whole page is not refreshed by re-making the `loadOrder` call each time.
    if (attempts === 0) {
      await loadOrder();
    } else {
      let response;

      if (route.params.share) {
        response = await useHttp().get(`/orders/shared/${route.params.share}`);
      } else if (route.params.order) {
        response = await useHttp().get(`/orders/${route.params.order}`);
      }

      if (response.statusCode === HttpStatusCode.Ok) {
        if (response.data.status !== order.value.status || response.data.items !== order.value.items) {
          order.value = response.data;
        }
      }
    }

    attempts++;

    // Will return true if any of the items on the order are missing the downloadable file.
    missingDownloadableFile = order.value.items?.some(item => {
      return item.downloadableFile === null;
    });

    if (order.value.status === 'Complete' && !missingDownloadableFile) {
      return;
    }

    // The API call will happen at constant intervals before hitting the `initialMaxAttempts`, after that there will be a back-off`
    // for the time between each call is made, increasing exponentially.
    if (attempts < initialMaxAttempts) {
      setTimeout(attemptLoad, initialInterval);
    } else {
      subsequentInterval = subsequentInterval * 2;
      setTimeout(attemptLoad, subsequentInterval);
    }
  };

  await attemptLoad();
};

refreshOrders();

const redirect = async () => {
  await router.push({ name: 'app_orders' });
};

</script>
